import type { WaitlistExperienceLevel, WaitlistSource } from '@prepzio/shared';
import { useReducedMotion } from 'motion/react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { track } from '../lib/analytics';
import { joinWaitlist, readUtmParams } from '../lib/api';

import { PixelButton } from './PixelButton';
import { Turnstile } from './Turnstile';
import { WaitlistProfileFields } from './WaitlistProfileFields';

type Status = 'idle' | 'loading' | 'success' | 'error';

type Props = {
  source: WaitlistSource;
  /** Visually hide the helper hint (used in tight spaces). */
  compact?: boolean;
  /** Override the submit button label. */
  cta?: string;
  /** Override the idle helper text below the form. */
  hint?: string;
  /** Override the success copy. */
  successMessage?: string;
  /** Override the message when the email is already on the waitlist. */
  alreadyJoinedMessage?: string;
  /** ID for scroll target / anchor linking. */
  anchorId?: string;
};

const TURNSTILE_SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ?? '';
const MAX_AI_TOOLS = 20;

const devTurnstileToken = () => (TURNSTILE_SITE_KEY ? null : 'dev-bypass');

export const WaitlistForm = ({
  source,
  compact = false,
  cta = 'Join the waitlist',
  hint,
  successMessage = 'Thank you! You’ve been added to the waitlist.',
  alreadyJoinedMessage = "You're already on the waitlist. We'll be in touch.",
  anchorId,
}: Props) => {
  const inputId = useId();
  const experienceId = useId();
  const mainPainId = useId();
  const mainPainCounterId = useId();
  const customToolId = useId();
  const statusId = useId();
  const modalTitleId = useId();
  const modalDescId = useId();
  const modalRef = useRef<HTMLDivElement>(null);

  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [experienceLevel, setExperienceLevel] = useState<WaitlistExperienceLevel | ''>('');
  const [selectedPresets, setSelectedPresets] = useState<Set<string>>(() => new Set());
  const [customTools, setCustomTools] = useState<string[]>([]);
  const [customToolInput, setCustomToolInput] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [mainPain, setMainPain] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState<string>('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(devTurnstileToken);
  const [showXp, setShowXp] = useState(false);
  const reducedMotion = useReducedMotion();

  const totalAiTools = selectedPresets.size + customTools.length;

  const buildAiTools = useCallback((): string[] => {
    const presets = Array.from(selectedPresets);
    return [...presets, ...customTools].slice(0, MAX_AI_TOOLS);
  }, [selectedPresets, customTools]);

  const resetProfileFields = () => {
    setExperienceLevel('');
    setSelectedPresets(new Set());
    setCustomTools([]);
    setCustomToolInput('');
    setShowOtherInput(false);
    setMainPain('');
  };

  const closeProfileStep = useCallback(() => {
    setProfileOpen(false);
    setTurnstileToken(devTurnstileToken());
  }, []);

  const handleTurnstileSuccess = useCallback((token: string) => setTurnstileToken(token), []);
  const handleTurnstileExpire = useCallback(() => setTurnstileToken(devTurnstileToken()), []);

  const togglePreset = (tool: string) => {
    setSelectedPresets((prev) => {
      const next = new Set(prev);
      if (next.has(tool)) {
        next.delete(tool);
      } else if (totalAiTools < MAX_AI_TOOLS) {
        next.add(tool);
      }
      return next;
    });
  };

  const addCustomTool = () => {
    const trimmed = customToolInput.trim().slice(0, 80);
    if (!trimmed) return;
    const all = buildAiTools();
    if (all.length >= MAX_AI_TOOLS) return;
    if (all.some((t) => t.toLowerCase() === trimmed.toLowerCase())) {
      setCustomToolInput('');
      return;
    }
    setCustomTools((prev) => [...prev, trimmed]);
    setCustomToolInput('');
  };

  useEffect(() => {
    if (status !== 'success' || reducedMotion) return;
    setShowXp(true);
    const id = window.setTimeout(() => setShowXp(false), 1200);
    return () => window.clearTimeout(id);
  }, [status, reducedMotion]);

  useEffect(() => {
    if (!profileOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeProfileStep();
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    const focusTimer = window.setTimeout(() => {
      modalRef.current?.querySelector<HTMLElement>('select, button, textarea, input')?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
      window.clearTimeout(focusTimer);
    };
  }, [profileOpen, closeProfileStep]);

  const handleEmailContinue = () => {
    if (status === 'loading' || status === 'success') return;

    if (!email.trim()) {
      setStatus('error');
      setMessage('Email is required.');
      track('waitlist_submit_error', { source, code: 'EMAIL_EMPTY' });
      return;
    }

    if (source === 'hero') track('hero_cta_click', { source });

    setStatus('idle');
    setMessage('');
    setProfileOpen(true);
  };

  const submitWaitlist = async () => {
    if (status === 'loading') return;

    if (!turnstileToken) {
      setStatus('error');
      setMessage('Please complete the security check.');
      track('waitlist_submit_error', { source, code: 'CAPTCHA_MISSING' });
      return;
    }

    setStatus('loading');
    setMessage('');

    const utm = readUtmParams();
    const aiTools = buildAiTools();
    const trimmedPain = mainPain.trim();

    const result = await joinWaitlist({
      email: email.trim(),
      source,
      website,
      turnstileToken,
      ...utm,
      ...(experienceLevel && { experienceLevel }),
      ...(aiTools.length > 0 && { aiTools }),
      ...(trimmedPain && { mainPain: trimmedPain }),
    });

    if (result.ok) {
      setStatus('success');
      setMessage(result.status === 'already_joined' ? alreadyJoinedMessage : successMessage);
      setEmail('');
      resetProfileFields();
      closeProfileStep();
      track('waitlist_submit_success', { source, status: result.status });
    } else {
      setStatus('error');
      setMessage(
        result.code === 'VALIDATION' ? 'Please enter a valid email address.' : result.message,
      );
      track('waitlist_submit_error', { source, code: result.code });
    }
  };

  const isSuccess = status === 'success';
  const isError = status === 'error';
  const fieldsDisabled = isSuccess || status === 'loading';

  const idleHint =
    hint ??
    (compact
      ? 'Free waitlist. No spam, no recruiters.'
      : 'Join the waitlist for your first skill check when beta opens. No spam, no recruiters.');
  const profileModal =
    profileOpen &&
    createPortal(
      <div className="waitlist-modal" onClick={closeProfileStep}>
        <div
          ref={modalRef}
          className="waitlist-modal__panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalTitleId}
          aria-describedby={modalDescId}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="waitlist-modal__header">
            <div>
              <h2 id={modalTitleId} className="waitlist-modal__title">
                Tell us about you
              </h2>
              <p id={modalDescId} className="waitlist-modal__desc">
                Optional: helps us tailor the beta. You can skip and join now.
              </p>
            </div>
            <button
              type="button"
              className="waitlist-modal__close"
              aria-label="Close"
              onClick={closeProfileStep}
            >
              ×
            </button>
          </div>

          <WaitlistProfileFields
            experienceId={experienceId}
            mainPainId={mainPainId}
            mainPainCounterId={mainPainCounterId}
            customToolId={customToolId}
            experienceLevel={experienceLevel}
            onExperienceLevelChange={setExperienceLevel}
            selectedPresets={selectedPresets}
            customTools={customTools}
            customToolInput={customToolInput}
            onCustomToolInputChange={setCustomToolInput}
            showOtherInput={showOtherInput}
            onToggleOtherInput={() => setShowOtherInput((v) => !v)}
            onTogglePreset={togglePreset}
            onAddCustomTool={addCustomTool}
            onRemoveCustomTool={(tool) => setCustomTools((prev) => prev.filter((t) => t !== tool))}
            mainPain={mainPain}
            onMainPainChange={setMainPain}
            totalAiTools={totalAiTools}
            disabled={fieldsDisabled}
          />

          {TURNSTILE_SITE_KEY && (
            <Turnstile
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={handleTurnstileSuccess}
              onExpire={handleTurnstileExpire}
            />
          )}

          {isError && message && (
            <p className="waitlist__hint waitlist__hint--err" role="alert">
              {message}
            </p>
          )}

          <div className="waitlist-modal__actions">
            <PixelButton
              type="button"
              variant="ghost"
              size="lg"
              disabled={status === 'loading'}
              onClick={submitWaitlist}
            >
              {status === 'loading' ? 'Joining…' : 'Skip & join'}
            </PixelButton>
            <PixelButton
              type="button"
              size="lg"
              disabled={status === 'loading' || !turnstileToken}
              onClick={submitWaitlist}
            >
              {status === 'loading' ? 'Joining…' : 'Join waitlist'}
            </PixelButton>
          </div>
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <form
        className="waitlist"
        onSubmit={(e) => e.preventDefault()}
        noValidate
        id={anchorId}
        aria-label="Join the Prepzio waitlist"
      >
        <div className="waitlist__row">
          <label className="sr-only" htmlFor={inputId}>
            Your email address
          </label>
          <input
            id={inputId}
            className="waitlist__input"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@dev.com"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby={statusId}
            aria-invalid={isError || undefined}
            disabled={isSuccess}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleEmailContinue();
              }
            }}
          />

          <PixelButton
            type="button"
            size="lg"
            disabled={status === 'loading' || isSuccess}
            onClick={handleEmailContinue}
          >
            {isSuccess ? 'Joined ✓' : cta}
          </PixelButton>
        </div>

        <div className="waitlist__honeypot" aria-hidden="true">
          <label>
            Leave this empty
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </label>
        </div>

        {showXp && (
          <span className="waitlist__xp" aria-hidden="true">
            +100 XP
          </span>
        )}

        <p
          id={statusId}
          className={`waitlist__hint${
            isError ? ' waitlist__hint--err' : isSuccess ? ' waitlist__hint--ok' : ''
          }`}
          aria-live="polite"
        >
          {isSuccess ? message : isError && !profileOpen ? message : idleHint}
        </p>

        {isSuccess && (
          <span className="waitlist__stage-clear" aria-hidden="true">
            Stage clear
          </span>
        )}
      </form>

      {profileModal}
    </>
  );
};
